// app/(booking)/booking/activities/[slug]/ActivityBookingClient.tsx
// Activity booking interface - simplified to single persons/pricePerPerson
// Transport activities (category === 'transport') are handled by TransportBookingClient.

'use client'

import { useState, useCallback } from 'react'
import { useTranslations, useLocale } from 'next-intl'
import type { Adventure } from '@/lib/data'
import { useActivity } from '@/lib/hooks/useActivities'
import SimpleDayCalendar from '@/components/booking/activities/SimpleDayCalendar'
import PersonSelector from '@/components/booking/activities/PersonSelector'
import AddToCartButton from '@/components/booking/activities/AddToCartButton'
import LodgeCrossSell from '@/components/booking/LodgeCrossSell'
import ServicesCrossSell from '@/components/booking/ServicesCrossSell'
import TransportBookingClient from './TransportBookingClient'
import styles from './ActivityBookingClient.module.css'

interface ActivityBookingClientProps {
  activity: Adventure
}

export default function ActivityBookingClient({ activity }: ActivityBookingClientProps) {
  const t = useTranslations('bookingActivity')
  const rawLocale = useLocale()
  const locale = (['en', 'es', 'pt'].includes(rawLocale) ? rawLocale : 'en') as 'en' | 'es' | 'pt'

  // Fetch pricing data from Firestore API
  const { activity: firestoreActivity, loading: pricingLoading, error: pricingError } = useActivity(activity.id)

  // State - simplified: single persons + pricePerPerson
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [pricePerPerson, setPricePerPerson] = useState(0)
  const [persons, setPersons] = useState(2)

  // Derive pricing from Firestore data
  const defaultPricePerPerson = firestoreActivity?.basePrice?.amount ?? 0
  const blockedDates = firestoreActivity?.blockedDates ?? []
  const dailyPricing = firestoreActivity?.dailyPricing ?? {}

  // Handle date selection - receives pricePerPerson from calendar
  const handleDateSelect = useCallback((date: string, priceInfo?: { pricePerPerson: number }) => {
    setSelectedDate(date)
    if (priceInfo) {
      setPricePerPerson(priceInfo.pricePerPerson)
    }
  }, [])

  // Handle person count update
  const handlePersonUpdate = useCallback((newPersons: number) => {
    setPersons(newPersons)
  }, [])

  // Transport activities: delegate to dedicated component
  // The Activity type from useActivity hook doesn't declare transport-specific
  // fields yet; they are present at runtime. Cast to pass through safely.
  if (firestoreActivity && firestoreActivity.category === 'transport') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (
      <TransportBookingClient
        activity={activity}
        firestoreActivity={firestoreActivity as Parameters<typeof TransportBookingClient>[0]['firestoreActivity']}
      />
    )
  }

  // Loading state while fetching pricing
  if (pricingLoading) {
    return (
      <section className={styles.bookingSection}>
        <div className={styles.container}>
          <div className={styles.bookingLayout}>
            <div className={styles.centerPanel}>
              <h2 className={styles.sectionTitle}>{t('selectDate')}</h2>
              <div className={styles.selectDatePrompt}>
                <p>{t('loadingPricing')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  // Error state
  if (pricingError || !firestoreActivity) {
    return (
      <section className={styles.bookingSection}>
        <div className={styles.container}>
          <div className={styles.bookingLayout}>
            <div className={styles.centerPanel}>
              <h2 className={styles.sectionTitle}>{t('selectDate')}</h2>
              <div className={styles.selectDatePrompt}>
                <p>{t('pricingError')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.bookingSection}>
      <div className={styles.container}>
        <div className={styles.bookingLayout}>
          {/* Left Panel - Calendar */}
          <div className={styles.centerPanel}>
            <h2 className={styles.sectionTitle}>{t('selectDate')}</h2>
            <SimpleDayCalendar
              activityId={activity.id}
              selectedDate={selectedDate}
              onSelectDate={handleDateSelect}
              maxMonths={6}
              defaultPricePerPerson={defaultPricePerPerson}
              blockedDates={blockedDates}
              dailyPricing={dailyPricing}
            />
          </div>

          {/* Right Panel - Guest Selector + Add to Cart */}
          <div className={styles.rightPanel}>
            <div className={styles.stickyContainer}>
              <div className={styles.panelCard}>
                {selectedDate && pricePerPerson > 0 ? (
                  <>
                    {/* Add to Cart FIRST - most important action */}
                    <div className={styles.actionSection}>
                      <AddToCartButton
                        activityId={activity.id}
                        activitySlug={activity.id}
                        activityName={activity.title}
                        activityImage={activity.heroImage}
                        date={selectedDate}
                        persons={persons}
                        pricePerPerson={pricePerPerson}
                      />
                    </div>

                    {/* Person selector below */}
                    <PersonSelector
                      persons={persons}
                      minPersons={1}
                      maxPersons={50}
                      pricePerPerson={pricePerPerson}
                      onUpdate={handlePersonUpdate}
                      showPricing={true}
                    />
                  </>
                ) : (
                  <div className={styles.selectDatePrompt}>
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <p>{t('selectDatePrompt')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Cross-Sell Panel - Services + Lodges (non-transport activities only) */}
          <div className={styles.crossSellPanel}>
            <ServicesCrossSell />
            <LodgeCrossSell
              activityId={activity.id}
              locale={locale}
              className={styles.lodgeCrossSell}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
